import { useAuth } from "@/auth.tsx";
import { ArtifactLayout } from "@/components/artifacts/index.tsx";
import { ArtifactPanelToggle } from "@/components/artifacts/artifact-panel-toggle.tsx";
import { ArtifactProvider } from "@/components/artifacts/artifact-provider.tsx";
import { CHAT_COLUMN_CLASS } from "@/components/chat/chat-column.ts";
import { ChatInterface } from "@/components/chat/chat-interface-websocket.tsx";
import { useChatArtifacts } from "@/components/chat/use-chat-artifacts.ts";
import { RouterButton } from "@/components/custom/router-button.tsx";
import { Badge } from "@/components/ui/badge.tsx";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip.tsx";
import { m } from "@/paraglide/messages.js";
import { chatSpaceIdQueryOptions } from "@/services/chats-service.ts";
import { projectQueryOptions } from "@/services/projects-service.ts";
import { createEntityErrorBoundary } from "@/utils/entity-error-boundary.tsx";
import type { FrontendChatMessage } from "@/types/chat.ts";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, Lock, User } from "lucide-react";
import { useState } from "react";
import { useDocumentTitle } from "@/hooks/use-document-title.ts";
import { z } from "zod";
import { pageTitle } from "@/utils/page-title.ts";
import { cn } from "@/utils/cn.ts";

export const Route = createFileRoute("/_authenticated/dashboard/chats/$chatId")(
	{
		component: RouteComponent,
		// Validate here (not in params.parse) so a malformed id throws inside the
		// loader, where the route's errorComponent catches it — param-parse throws
		// happen during matching and escape to the global boundary instead.
		loader: ({ params }) => {
			z.uuid().parse(params.chatId);
		},
		validateSearch: z.object({
			artifactId: z.string().uuid().optional(),
			voice: z.boolean().optional(),
		}),
		errorComponent: createEntityErrorBoundary({
			title: m.chat_not_found_title,
			description: m.chat_not_found_description,
			backLabel: m.chat_not_found_back,
			backTo: "/dashboard/chats",
		}),
		head: () => ({
			meta: [{ title: pageTitle() }],
		}),
	},
);

function RouteComponent() {
	const { chatId } = Route.useParams();
	// The tab names the chat, and follows it when it is named or renamed. The
	// query is the one the chat interface already holds, so it costs no request.
	const { data: chat } = useQuery(chatSpaceIdQueryOptions(chatId));
	useDocumentTitle(chat ? pageTitle(chat.name) : undefined);
	// The artifact panel lists what this chat made or opened, so the messages
	// are lifted out of the chat to the provider that feeds it.
	const [messages, setMessages] = useState<FrontendChatMessage[]>([]);
	const chatArtifacts = useChatArtifacts(messages);

	return (
		<ArtifactProvider chatId={chatId} chatArtifacts={chatArtifacts}>
			<ArtifactLayout chatId={chatId} corner={<ArtifactPanelToggle />}>
				<ChatInterface
					key={chatId}
					chatId={chatId}
					onMessagesChange={setMessages}
				/>
				{/* Floating top-left controls: overlay the chat column so they
				    stay visible across every layout without consuming chat width. */}
				<div
					className={cn(
						CHAT_COLUMN_CLASS,
						"pointer-events-none absolute inset-x-0 top-3 z-30 flex items-center gap-2",
					)}
				>
					<ChatBackNavigation chatId={chatId} />
					<ChatOwnerBadge chatId={chatId} />
					<RagInternalOnlyBadge chatId={chatId} />
				</div>
			</ArtifactLayout>
		</ArtifactProvider>
	);
}

/**
 * Names whose chat this is, when the reader is not that person.
 *
 * Only `chats.user_id` can append to a chat — everyone else who opens one
 * through project membership forks it — so a reader of someone else's chat is
 * looking at a conversation they did not have. The per-message author labels say
 * that too, but only once you reach a labelled message; this says it up front.
 *
 * Silent on your own chats, which is every chat for most people.
 */
function ChatOwnerBadge({ chatId }: { chatId: string }) {
	const { data: chatData } = useQuery(chatSpaceIdQueryOptions(chatId));
	if (chatData === undefined || chatData.isOwner || !chatData.ownerName) {
		return null;
	}
	return (
		<Tooltip>
			<TooltipTrigger
				render={
					<Badge variant="secondary" className="pointer-events-auto gap-1.5">
						<User className="h-3 w-3" />
						{chatData.ownerName}
					</Badge>
				}
			/>
			<TooltipContent>
				{m.chat_owner_badge_tooltip({ name: chatData.ownerName })}
			</TooltipContent>
		</Tooltip>
	);
}

/**
 * E1: surfaces a Lock-icon badge in the chat header when the resolved RAG
 * mode is "internal_only". Renders nothing for "default" / "prefer_internal"
 * — the user-facing surface should be silent when the org runs vanilla
 * behaviour.
 */
function RagInternalOnlyBadge({ chatId }: { chatId: string }) {
	const { data: chatData } = useQuery(chatSpaceIdQueryOptions(chatId));
	if (chatData?.ragMode !== "internal_only") {
		return null;
	}
	return (
		<Tooltip>
			<TooltipTrigger
				render={
					<Badge variant="secondary" className="pointer-events-auto gap-1.5">
						<Lock className="h-3 w-3" />
						{m.rag_internal_only_badge()}
					</Badge>
				}
			/>
			<TooltipContent>{m.rag_internal_only_badge_tooltip()}</TooltipContent>
		</Tooltip>
	);
}

function ChatBackNavigation({ chatId }: { chatId: string }) {
	const { user } = useAuth();
	const { data: chatData } = useQuery(chatSpaceIdQueryOptions(chatId));
	const spaceId = chatData?.spaceId;
	const isPersonalProject = spaceId === user?.personalProject;

	const { data: project } = useQuery({
		...projectQueryOptions(spaceId ?? ""),
		enabled: !!spaceId && !isPersonalProject,
	});

	if (!spaceId || isPersonalProject || !project) {
		return null;
	}

	return (
		<Tooltip>
			<TooltipTrigger
				render={
					<RouterButton
						to="/dashboard/projects/$projectId"
						params={{ projectId: spaceId }}
						variant="outline"
						size="icon"
						className="pointer-events-auto h-8 w-8 shadow-sm"
					>
						<ArrowLeft className="h-4 w-4" />
					</RouterButton>
				}
			/>
			<TooltipContent>
				{m.navigation_back_to_project({ name: project.name })}
			</TooltipContent>
		</Tooltip>
	);
}
